import { bindPopover } from 'material-ui-popup-state'
import { Box, Popover, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { SquareButton } from '../../../styles/ButtonStyles'
import { Clear } from '@mui/icons-material'
import React, { MutableRefObject, useState } from 'react'
import { t } from 'i18next'
import { PopupState } from 'material-ui-popup-state/hooks'
import Cost from '../../../store/types/models/Cost'
import { DuplicateCost, useDuplicateCostsMutation } from '../../../store/redux/reducers/costSlice'
import DateFormat from '../../../store/types/enums/DateFormat'
import { createErrorMessage } from '../../../store/utils'
import { useNotification } from '../../../store/redux/hooks'
import ConfirmButton from '../../Button/ConfirmButton'
import { Trans } from 'react-i18next'

type CostDuplicatePopupProps = {
  targetRef: MutableRefObject<null>
  popupState: PopupState
  month: Dayjs
  selectedCosts: Cost[]
  setDuplicateMode: React.Dispatch<React.SetStateAction<boolean>>
}

const CostDuplicatePopup = ({
  targetRef,
  popupState,
  month,
  selectedCosts,
  setDuplicateMode,
}: CostDuplicatePopupProps) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(month.add(1, 'month'))
  const [loading, setLoading] = useState<boolean>(false)
  const [duplicateCosts] = useDuplicateCostsMutation()
  const { displayNotification } = useNotification()

  const handleDuplicate = async (data: DuplicateCost) => {
    try {
      await duplicateCosts(data)
      displayNotification({ message: t('components.costSidebar.alert.costsDuplicated'), type: 'success' })
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    } finally {
      setLoading(false)
      popupState.close()
      setDuplicateMode(false)
    }
  }

  const handleApplyButtonClick = () => {
    setLoading(true)
    const costsIds = selectedCosts.map(cost => cost.id)
    const date = selectedDate || month.add(1, 'month')
    handleDuplicate({ costIds: costsIds, date: date.format(DateFormat.YearMonth) })
  }

  return (
    <Popover
      onClick={e => {
        e.stopPropagation()
      }}
      {...bindPopover(popupState)}
      anchorEl={targetRef.current}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box
        display={'flex'}
        padding={'16px'}
        borderRadius={'8px'}
        alignItems={'center'}
        gap={'8px'}
        flexDirection={'column'}
      >
        <DatePicker
          views={['month', 'year']}
          onChange={e => setSelectedDate(e)}
          value={selectedDate}
          label={t('components.costSidebar.table.body.startDate') + ' *'}
          slotProps={{ textField: { size: 'small' } }}
          minDate={dayjs().startOf('month')}
          defaultValue={month.add(1, 'month')}
          sx={{ svg: { color: 'text.mediumEmphasis' } }}
        />
        <Box maxWidth={300} textAlign="center">
          <Typography variant="caption">
            <Trans
              i18nKey="components.costSidebar.duplicateCaption"
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Box>
        <Box display={'flex'} gap={2}>
          <ConfirmButton
            text={t('page.projectDistribution.apply')}
            onClick={() => {
              handleApplyButtonClick()
            }}
            isLoadingButton={true}
            isLoading={loading}
            isDisabled={loading}
          />
          <SquareButton
            onClick={() => {
              popupState.close()
            }}
            size="small"
            color="secondary"
          >
            <Clear />
          </SquareButton>
        </Box>
      </Box>
    </Popover>
  )
}

export default CostDuplicatePopup